// src/modules/app-views/fiscal-config/pages/Document/tabs/NfceTab/index.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useState, useCallback, useEffect } from 'react';
import { Alert } from 'antd';
import { StopOutlined } from '@ant-design/icons';
import { openModal } from 'components/core/GenericModal';
import SimpleLoadingArea from 'components/SimpleComponents/SimpleLoadingArea';
import { useDocumentContext } from 'components/core/DocumentProvider';
import { useSimpleFormContext } from 'components/SimpleComponents/SimpleFormProvider';
import useZustand from 'hooks/useZustand';
import appService from 'services/appService';
import axiosService from 'services/axiosService';
import fiscalService from 'services/fiscalService';
import { API } from 'constants/Api';
import translateX from 'utils/translateX';
import CscTokenModal from 'modules/app-views/account/pages/Subscription/modals/CscTokenModal';
import showVoidNfceModal from './modals/VoidNfceModal';

import NfceCard from './NfceCard';
import VoidNfceCard from './VoidNfceCard';
import { useFiscalState } from 'modules/app-views/fiscalConfig/pages/Document/context/FiscalStateContext';

// *******************************************************************************
// CONSTANTS
// *******************************************************************************
const RESOURCE_NFCE = 'moduleresources_nfce';

// Fields required to activate NFCe service with their error messages
// Note: CSC token is encrypted in backend and never sent to frontend
// We check for 'token' (new) OR 'tokenMasked' (already configured)
const NFCE_REQUIRED_FIELDS = [
  { path: ['fiscalData', 'nfce', 'provider'], messageKey: 'inform_nfce_provider' },
  { path: ['fiscalData', 'nfce', 'environment'], messageKey: 'inform_nfce_environment' },
  { path: ['fiscalData', 'nfce', 'currentSerie'], messageKey: 'inform_nfce_serie' },
  { path: ['fiscalData', 'nfce', 'csc', 'id'], messageKey: 'inform_nfce_csc_id' },
  {
    path: ['fiscalData', 'nfce', 'csc', 'token'],
    altPath: ['fiscalData', 'nfce', 'csc', 'tokenMasked'], // Alternative: token already configured
    messageKey: 'inform_nfce_csc_token',
  },
];

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const NfceTab = ({ setState, state = {}, ...restProps }) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const storedSubscription = useZustand('auth', 'subscription');
  const { data, loading, refresh } = useDocumentContext();
  const { form, locked, goFocus } = useSimpleFormContext();
  const { setServiceEnabled } = useFiscalState();

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. HOOKS & REFS
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL VARIABLES I
  // ─────────────────────────────────────────────────────────────────────────────
  const subscriptionResources = storedSubscription?.resources || [];
  const hasNfceResource = subscriptionResources.includes(RESOURCE_NFCE);
  // Get UF (state) for SEFAZ links
  const entityUf = data?.address?.region || null;
  const cscLinkUrl = fiscalService.getCscLinks(entityUf, 'url');
  const cscLinkName = fiscalService.getCscLinks(entityUf, 'name');
  const entityId = data?.entityId || data?._id;

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const [nfceEnabled, setNfceEnabled] = useState(data?.fiscalData?.nfce?.isEnabled || false);
  const [validating, setValidating] = useState(false);
  const [validationError, setValidationError] = useState(null);

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. EFFECTS
  // ─────────────────────────────────────────────────────────────────────────────
  // Sync NFCe enabled state when data changes
  useEffect(() => {
    if (data?.fiscalData?.nfce?.isEnabled !== undefined) {
      setNfceEnabled(data.fiscalData.nfce.isEnabled);
    }
  }, [data?.fiscalData?.nfce?.isEnabled]);

  // Sync CSC tokenMasked field with API data
  useEffect(() => {
    if (!form || !data) return;

    const apiTokenMasked = data?.fiscalData?.nfce?.csc?.tokenMasked;
    const formTokenMasked = form.getFieldValue(['fiscalData', 'nfce', 'csc', 'tokenMasked']);

    // If API data doesn't have tokenMasked but form does, clear the form field
    if (!apiTokenMasked && formTokenMasked) {
      form.setFieldValue(['fiscalData', 'nfce', 'csc', 'tokenMasked'], undefined);
    }
    // If API data has tokenMasked and differs from form, update the form
    else if (apiTokenMasked && apiTokenMasked !== formTokenMasked) {
      form.setFieldValue(['fiscalData', 'nfce', 'csc', 'tokenMasked'], apiTokenMasked);
    }
  }, [data, form]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────

  // Silent save helper - saves isEnabled state without changing document mode
  const saveServiceEnabled = useCallback(
    async isEnabled => {
      if (!entityId) return;
      try {
        await axiosService.put({
          url: API.ENTITIES.URL.replace(':_id?', entityId),
          data: { 'fiscalData.nfce.isEnabled': isEnabled },
        });
      } catch (error) {
        console.error('[NfceTab] Silent save error:', error);
      }
    },
    [entityId]
  );

  const handleNfceToggle = useCallback(
    async checked => {
      // Clear previous validation error
      setValidationError(null);

      // If turning OFF, update local state and save silently
      if (!checked) {
        setNfceEnabled(false);
        setServiceEnabled('nfce', false); // Update context for CardHeader tags
        if (form) {
          form.setFieldValue(['fiscalData', 'nfce', 'isEnabled'], false);
        }
        appService.message({ flag: 's', text: 'service_deactivated', duration: 2 });
        return;
      }

      // If turning ON, validate form fields first
      if (!entityId) {
        appService.notification({
          flag: 'error',
          title: 'error',
          description: 'entity_not_found',
        });
        return;
      }

      // Show loading message (will be updated based on results)
      const msg = await appService.message({
        flag: 'loading',
        text: 'validating_nfce_data',
        duration: 0,
      });

      setValidating(true);

      // ─────────────────────────────────────────────────────────────────────────
      // PROCESS 1: Validate required fields (MANUAL - fields don't have rules)
      // ─────────────────────────────────────────────────────────────────────────
      const emptyFields = [];
      NFCE_REQUIRED_FIELDS.forEach(({ path, altPath, messageKey }) => {
        const value = form?.getFieldValue(path);
        const isEmpty = value === undefined || value === null || value === '';

        // Check alternative path if main path is empty (e.g., tokenMasked for already configured token)
        let altIsEmpty = true;
        if (isEmpty && altPath) {
          const altValue = form?.getFieldValue(altPath);
          altIsEmpty = altValue === undefined || altValue === null || altValue === '';
        }

        if (isEmpty && altIsEmpty) {
          emptyFields.push({ name: path, messageKey });
        }
      });

      if (emptyFields.length > 0) {
        const firstEmptyField = emptyFields[0];
        const errorMessageKey = firstEmptyField.messageKey;

        msg.update({
          flag: 'warning',
          text: errorMessageKey,
          duration: 4,
        });

        // Mark empty fields as error in the form (show red border)
        const fieldsToMark = emptyFields.map(({ name }) => ({
          name,
          errors: [translateX('required')],
        }));
        form.setFields(fieldsToMark);

        // Use goFocus to focus the first empty required field
        if (goFocus && firstEmptyField.name) {
          const fieldPath = firstEmptyField.name.join('.');
          goFocus(fieldPath);
        }

        setValidating(false);
        return;
      }

      // ─────────────────────────────────────────────────────────────────────────
      // PROCESS 2: Backend validation (form passed)
      // ─────────────────────────────────────────────────────────────────────────
      try {
        const formValues = form?.getFieldsValue(true) || {};
        const nfceData = formValues?.fiscalData?.nfce || {};

        const payload = {
          entityId,
          service: 'nfce',
          provider: nfceData.provider,
          fiscalData: {
            nfce: {
              environment: nfceData.environment,
              currentSerie: nfceData.currentSerie,
              csc: {
                id: String(nfceData.csc?.id || ''),
                token: nfceData.csc?.token || '',
              },
            },
          },
        };

        const [response] = await axiosService.post({
          url: API.FISCAL.SETUP_VALIDATE_SERVICE,
          data: payload,
          returnAsObject: true,
        });

        if (response?.isValid) {
          setNfceEnabled(true);
          setServiceEnabled('nfce', true); // Update context for CardHeader tags
          if (form) {
            form.setFieldValue(['fiscalData', 'nfce', 'isEnabled'], true);
          }

          msg.update({
            flag: 'success',
            text: 'service_enabled',
            duration: 3,
          });
        } else {
          const errors = response?.errorList || [];
          const errorMessages = errors
            .map(e => {
              const translated = translateX(e.i18nKey);
              return translated && translated !== e.i18nKey ? translated : e.message || e.i18nKey;
            })
            .join('\n');

          msg.update({
            flag: 'error',
            text: 'nfce_configuration_not_validated',
            duration: 4,
          });

          setValidationError(errorMessages || translateX('validation_failed_unknown'));
        }
      } catch (error) {
        console.error('[NfceTab] Validation error:', error);

        const errorMessage =
          error.response?.data?.message ||
          error.response?.data?.error?.message ||
          translateX('validation_error_occurred');

        msg.update({
          flag: 'error',
          text: 'nfce_configuration_not_validated',
          duration: 4,
        });

        setValidationError(errorMessage);
      } finally {
        setValidating(false);
      }
    },
    [entityId, form, goFocus, saveServiceEnabled, setServiceEnabled]
  );

  const handleOpenCscTokenModal = useCallback(() => {
    openModal(CscTokenModal, {
      uf: entityUf,
      onSuccess: ({ token, tokenMasked }) => {
        // Update form with both token (for PUT) and tokenMasked (for display)
        if (form) {
          form.setFieldValue(['fiscalData', 'nfce', 'csc', 'token'], token);
          form.setFieldValue(['fiscalData', 'nfce', 'csc', 'tokenMasked'], tokenMasked);
        }

        appService.message({
          flag: 's',
          text: 'csc_token_updated',
          duration: 2,
        });
      },
    });
  }, [form, entityUf]);

  const handleOpenVoidNfceModal = useCallback(() => {
    showVoidNfceModal({
      company: data,
      onSuccess: ({ protocolNumber }) => {
        refresh?.();
      },
    });
  }, [data, refresh]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 7. LOCAL VARIABLES II
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 8. UI
  // ─────────────────────────────────────────────────────────────────────────────
  // If resource is not contracted, show alert
  if (!hasNfceResource) {
    return (
      <div className="container" style={{ position: 'relative', minHeight: 120 }}>
        <Alert
          type="warning"
          showIcon
          icon={<StopOutlined />}
          title={translateX('module_not_contracted')}
          description={translateX('nfce_module_not_contracted_description')}
          style={{ marginTop: 16 }}
        />
      </div>
    );
  }

  return (
    <div className="container" style={{ position: 'relative', minHeight: 120 }}>
      <SimpleLoadingArea name="nfceData" isFetching={loading} data={data} />

      <NfceCard
        nfceEnabled={nfceEnabled}
        locked={locked}
        validating={validating}
        validationError={validationError}
        cscLinkUrl={cscLinkUrl}
        cscLinkName={cscLinkName}
        onToggle={handleNfceToggle}
        onOpenCscTokenModal={handleOpenCscTokenModal}
      />

      <VoidNfceCard onOpenVoidNfceModal={handleOpenVoidNfceModal} />
    </div>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default NfceTab;
